import React, { useState, useCallback } from 'react';
import { Building2 } from 'lucide-react';
import { router } from '@inertiajs/react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { FormField } from './ui/form_field';
import { Modal, ModalHeader, ModalBody, ModalFooter } from './ui/Modal';
import { getFormIconClassName } from '@/lib/utils';

interface TeamWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function TeamWorkspaceModal({ isOpen, onClose }: TeamWorkspaceModalProps) {
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
      setName('');
      setErrors({});
    }
  }, [isSubmitting, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    router.post('/workspaces', {
      workspace_type: 'team',
      organization: { name }
    }, {
      onError: (errors) => {
        const formattedErrors: Record<string, string> = {};

        // Handle validation errors
        Object.entries(errors).forEach(([key, value]) => {
          formattedErrors[key] = Array.isArray(value) ? value[0] : value;
        });

        setErrors(formattedErrors);
        setIsSubmitting(false);
      },
      onFinish: () => {
        setIsSubmitting(false);
      }
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      canClose={!isSubmitting}
      size="md"
    >
      <ModalHeader
        title="Create Team Workspace"
        subtitle="Enter a name for your new team workspace."
        icon={<Building2 className="w-6 h-6 text-primary dark:text-primary-light" />}
      />

      <form onSubmit={handleSubmit} className="space-y-4">
        <ModalBody>
          <FormField
            id="workspace-name"
            label="Workspace name"
            error={errors['organization.name'] || errors['organization']}
          >
            <Input
              id="workspace-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme Inc."
              aria-invalid={!!(errors['organization.name'] || errors['organization'])}
              leftIcon={
                <Building2
                  className={getFormIconClassName(!!(errors['organization.name'] || errors['organization']))}
                />
              }
              autoFocus
            />
          </FormField>
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting}
            loadingText="Creating..."
          >
            Create workspace
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}